import React, { useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";

import { usePhoneVerificationModal } from "../hooks/usePhoneVerificationModal";
import { PhoneVerificationForm } from "./PhoneVerificationForm";
import { CodeVerificationForm } from "./CodeVerificationForm";
import { useStores } from "stores/setup/use-store";
import { useTranslation } from "react-i18next";

interface PhoneVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (code?: string, phone?: string) => void;
  title?: string;
  description?: string;
  confirmButtonText?: string;
  initialPhoneData?: { phone?: string; code?: string };
  isPhoneAlreadyVerified?: boolean;
}

export const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> =
  observer(
    ({
      isOpen,
      onClose,
      onSuccess,
      title,
      description,
      confirmButtonText,
      initialPhoneData,
      isPhoneAlreadyVerified,
    }) => {
      const { t } = useTranslation("translation", {
        keyPrefix: "userPhoneEmailVerify",
      });

      const {
        dataStores: { userConfigStore },
      } = useStores();

      const {
        step,
        setStep,
        phoneData,
        setPhoneData,
        enteredCode,
        setEnteredCode,
        isCodeValid,
        resendTimer,
        isBlocked,
        isLoading,
        handleSendCode,
        handleResendCode,
        handleCodeSubmit,
        resetState,
        setResendTimer,
        consentChecked,
        setConsentChecked,
        isPhoneAlreadyUsed,
        setIsPhoneAlreadyUsed,
      } = usePhoneVerificationModal();

      useEffect(() => {
        if (initialPhoneData) {
          setPhoneData(initialPhoneData);
        }
      }, [initialPhoneData, setPhoneData]);

      const handleConfirm = async () => {
        if (phoneData.code && phoneData.phone) {
          const response = await userConfigStore.verifyEmailAndPhoneInUse({
            phone_code: phoneData.code,
            phone_number: phoneData.phone,
          });

          if (!response?.success) {
            if (response?.field === "phone") {
              setIsPhoneAlreadyUsed(true);
            }
            return;
          }
        }

        await handleSendCode();
        setStep("code");
        setResendTimer(30);
        userConfigStore.updateUserConcetTermsWhatsappAlerts(
          consentChecked,
          t("iAgreeReceive"),
          "v1"
        );
      };

      const handleBack = () => {
        setStep("form");
        setEnteredCode("");
      };

      const handleSubmitCode = async () => {
        const success = await handleCodeSubmit();
        if (success) {
          onClose();
          resetState();

          if (onSuccess) {
            const { code, phone } = phoneData;
            onSuccess(code, phone);
          }
        }
      };

      const handleClose = () => {
        resetState();
        onClose();
      };

      return (
        <Modal
          closeOnOverlayClick={false}
          isCentered
          isOpen={isOpen}
          onClose={handleClose}
        >
          <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
          <ModalContent>
            <ModalCloseButton color="gray.500" />
            <ModalBody py="24px" px="32px" color="black">
              {step === "form" && (
                <PhoneVerificationForm
                  phoneData={phoneData}
                  setPhoneData={setPhoneData}
                  onConfirm={handleConfirm}
                  isLoading={isLoading}
                  title={title}
                  description={description}
                  confirmButtonText={confirmButtonText}
                  isPhoneAlreadyUsed={isPhoneAlreadyUsed}
                  consentChecked={consentChecked}
                  setConsentChecked={setConsentChecked}
                  isPhoneAlreadyVerified={isPhoneAlreadyVerified}
                />
              )}

              {step === "code" && (
                <CodeVerificationForm
                  phoneData={phoneData}
                  enteredCode={enteredCode}
                  setEnteredCode={setEnteredCode}
                  isCodeValid={isCodeValid}
                  resendTimer={resendTimer}
                  isBlocked={isBlocked}
                  isLoading={isLoading}
                  onBack={handleBack}
                  onSubmit={handleSubmitCode}
                  onResendCode={handleResendCode}
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      );
    }
  );
